import React, { useEffect, useRef, useState } from 'react';
import JSZip from 'jszip';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import BrushIcon from '@mui/icons-material/Brush';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import {
  bulkDeleteDataExchangeEntries,
  deleteDataExchangeEntry,
  createDataExchangeGuestLink,
  createDataExchangeAnnotation,
  deleteDataExchangeAnnotation,
  getDataExchangeBlob,
  listDataExchangeEntries,
  listDataExchangeAnnotations,
  listDataExchangeGuestLinks,
  revokeDataExchangeGuestLink,
  updateDataExchangeAnnotation,
  updateDataExchangeEntry,
  uploadDataExchangeEntry,
} from '../../api/dataExchange';
import { canManageDataExchange } from '../../config/roleAccess';
import { useAuth } from '../../contexts/AuthContext';
import DataExchangeAnnotationEditor from './DataExchangeAnnotationEditor';
import DataExchangeBulkDeleteDialog from './DataExchangeBulkDeleteDialog';
import { createAnnotatedImageBlob } from './dataExchangeAnnotationCanvas';

function formatDateTime(value) {
  if (!value) return '-';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? String(value)
    : parsed.toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
}

function mediaLabel(entry) {
  return entry.title || entry.original_name || 'Ohne Titel';
}

function ImagePreview({ entry }) {
  const [url, setUrl] = useState('');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (entry.asset_kind !== 'image') return undefined;
    let active = true;
    let objectUrl = '';
    getDataExchangeBlob(entry.id)
      .then((response) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(response.data);
        setUrl(objectUrl);
      })
      .catch(() => active && setFailed(true));
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [entry.asset_kind, entry.id]);

  if (failed || !url) {
    return (
      <Box sx={{ height: 190, display: 'grid', placeItems: 'center', bgcolor: '#eef1f3', color: 'text.secondary' }}>
        {failed ? <Typography variant="body2">Vorschau nicht verfügbar</Typography> : <CircularProgress size={28} />}
      </Box>
    );
  }

  return <Box component="img" src={url} alt={mediaLabel(entry)} sx={{ width: '100%', height: 190, objectFit: 'cover', display: 'block' }} />;
}

function MediaCard({ entry, canManage, selectable, selected, onToggleSelection, onOpen, onEdit, onDelete }) {
  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 1, position: 'relative', outline: selected ? '3px solid' : 'none', outlineColor: 'primary.main' }}>
        {selectable ? <Tooltip title={selected ? 'Auswahl aufheben' : 'Medium auswählen'}>
          <Checkbox
            checked={selected}
            onChange={onToggleSelection}
            inputProps={{ 'aria-label': `${mediaLabel(entry)} auswählen` }}
            sx={{ position: 'absolute', top: 6, right: 6, zIndex: 2, bgcolor: 'rgba(255,255,255,.9)', borderRadius: 1, p: 0.5 }}
          />
          </Tooltip> : null}
      <CardActionArea onClick={onOpen} sx={{ position: 'relative' }}>
        {entry.asset_kind === 'image' ? (
          <ImagePreview entry={entry} />
        ) : (
          <Box sx={{ height: 190, display: 'grid', placeItems: 'center', bgcolor: '#20272d', color: '#fff' }}>
            <PlayCircleOutlineIcon sx={{ fontSize: 64 }} />
          </Box>
        )}
        <Box sx={{ position: 'absolute', top: 8, left: 8, px: 1, py: 0.25, bgcolor: 'rgba(0,0,0,.7)', color: '#fff', fontSize: 12 }}>
          {entry.asset_kind === 'video' ? 'Video' : 'Foto'}
        </Box>
      </CardActionArea>
      <CardContent sx={{ p: 1.75, flexGrow: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{mediaLabel(entry)}</Typography>
        {entry.remarks ? <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{entry.remarks}</Typography> : null}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
          {entry.created_by_name} · {formatDateTime(entry.created_at)}
        </Typography>
      </CardContent>
      {canManage ? (
        <Stack direction="row" justifyContent="flex-end" sx={{ px: 1, pb: 1 }}>
          <Tooltip title="Überschrift und Bemerkung bearbeiten"><IconButton onClick={onEdit} aria-label="Anhang bearbeiten"><EditOutlinedIcon /></IconButton></Tooltip>
          <Tooltip title="Anhang löschen"><IconButton color="error" onClick={onDelete} aria-label="Anhang löschen"><DeleteOutlineIcon /></IconButton></Tooltip>
        </Stack>
      ) : null}
    </Card>
  );
}

function SlideshowDialog({ entries, index, canManage, onIndexChange, onClose }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [mediaUrl, setMediaUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [annotations, setAnnotations] = useState([]);
  const [selectedAnnotationId, setSelectedAnnotationId] = useState(null);
  const [annotatedUrl, setAnnotatedUrl] = useState('');
  const [editorAnnotation, setEditorAnnotation] = useState(undefined);
  const [editorOpen, setEditorOpen] = useState(false);
  const [savingAnnotation, setSavingAnnotation] = useState(false);
  const videoRef = useRef(null);
  const entry = index === null ? null : entries[index];
  const selectedAnnotation = annotations.find((item) => item.id === selectedAnnotationId) || null;

  useEffect(() => {
    if (!entry) return undefined;
    let active = true;
    let objectUrl = '';
    setLoading(true);
    setError('');
    setMediaUrl('');
    getDataExchangeBlob(entry.id)
      .then((response) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(response.data);
        setMediaUrl(objectUrl);
      })
      .catch(() => active && setError('Das Medium konnte nicht geladen werden.'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
      videoRef.current?.pause();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [entry]);

  useEffect(() => {
    setAnnotations([]);
    setSelectedAnnotationId(null);
    if (!entry || entry.asset_kind !== 'image') return undefined;
    let active = true;
    listDataExchangeAnnotations(entry.id)
      .then((response) => {
        if (!active) return;
        const nextAnnotations = response.data.annotations || [];
        setAnnotations(nextAnnotations);
        setSelectedAnnotationId(nextAnnotations[0]?.id ?? null);
      })
      .catch(() => active && setError('Markierungen konnten nicht geladen werden.'));
    return () => { active = false; };
  }, [entry]);

  useEffect(() => {
    let active = true;
    let objectUrl = '';
    setAnnotatedUrl('');
    if (!mediaUrl || !selectedAnnotation) return undefined;
    createAnnotatedImageBlob(mediaUrl, selectedAnnotation.document)
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setAnnotatedUrl(objectUrl);
      })
      .catch(() => active && setError('Markierte Fassung konnte nicht dargestellt werden.'));
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [mediaUrl, selectedAnnotation]);

  useEffect(() => {
    if (!entry) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft') onIndexChange((index - 1 + entries.length) % entries.length);
      if (event.key === 'ArrowRight') onIndexChange((index + 1) % entries.length);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [entries.length, entry, index, onIndexChange]);

  const handleDownload = async () => {
    const response = await getDataExchangeBlob(entry.id, true);
    const url = URL.createObjectURL(response.data);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = entry.original_name;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const handleMarkedDownload = async () => {
    if (!selectedAnnotation) return;
    const blob = await createAnnotatedImageBlob(mediaUrl, selectedAnnotation.document);
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    const baseName = String(entry.original_name || 'foto').replace(/\.[^.]+$/, '');
    anchor.href = url;
    anchor.download = `${baseName}-markiert.png`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const saveAnnotation = async (payload) => {
    setSavingAnnotation(true);
    try {
      const response = editorAnnotation
        ? await updateDataExchangeAnnotation(entry.id, editorAnnotation.id, payload)
        : await createDataExchangeAnnotation(entry.id, payload);
      const saved = response.data.annotation;
      setAnnotations((current) => editorAnnotation
        ? current.map((item) => item.id === saved.id ? saved : item)
        : [saved, ...current]);
      setSelectedAnnotationId(saved.id);
      setEditorOpen(false);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Markierung konnte nicht gespeichert werden.');
    } finally {
      setSavingAnnotation(false);
    }
  };

  const removeAnnotation = async () => {
    if (!selectedAnnotation || !window.confirm('Diese Markierungsversion dauerhaft löschen?')) return;
    try {
      await deleteDataExchangeAnnotation(entry.id, selectedAnnotation.id);
      setAnnotations((current) => {
        const remaining = current.filter((item) => item.id !== selectedAnnotation.id);
        setSelectedAnnotationId(remaining[0]?.id ?? null);
        return remaining;
      });
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Markierung konnte nicht gelöscht werden.');
    }
  };

  return (
    <Dialog open={Boolean(entry)} onClose={onClose} fullScreen={fullScreen} maxWidth="lg" fullWidth PaperProps={{ sx: { bgcolor: '#15191d', color: '#fff', borderRadius: fullScreen ? 0 : 1 } }}>
      {entry ? (
        <>
          <Box sx={{ minHeight: fullScreen ? '65vh' : 580, position: 'relative', display: 'grid', placeItems: 'center', p: { xs: 1, sm: 4 } }}>
            <Tooltip title="Schließen"><IconButton onClick={onClose} sx={{ position: 'absolute', top: 8, right: 8, color: '#fff', zIndex: 2 }}><CloseIcon /></IconButton></Tooltip>
            {loading ? <CircularProgress sx={{ color: '#fff' }} /> : null}
            {error ? <Alert severity="error">{error}</Alert> : null}
            {!loading && mediaUrl && entry.asset_kind === 'image' ? <Box component="img" src={annotatedUrl || mediaUrl} alt={mediaLabel(entry)} sx={{ maxWidth: '100%', maxHeight: '72vh', objectFit: 'contain' }} /> : null}
            {!loading && mediaUrl && entry.asset_kind === 'video' ? <Box ref={videoRef} component="video" src={mediaUrl} controls autoPlay sx={{ maxWidth: '100%', maxHeight: '72vh' }} /> : null}
            {entries.length > 1 ? (
              <>
                <IconButton onClick={() => onIndexChange((index - 1 + entries.length) % entries.length)} aria-label="Vorheriger Anhang" sx={{ position: 'absolute', left: 8, color: '#fff', bgcolor: 'rgba(0,0,0,.45)' }}><ArrowBackIosNewIcon /></IconButton>
                <IconButton onClick={() => onIndexChange((index + 1) % entries.length)} aria-label="Nächster Anhang" sx={{ position: 'absolute', right: 8, color: '#fff', bgcolor: 'rgba(0,0,0,.45)' }}><ArrowForwardIosIcon /></IconButton>
              </>
            ) : null}
          </Box>
          <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, bgcolor: '#fff', color: 'text.primary' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
              <Box><Typography variant="h6" sx={{ fontWeight: 700 }}>{mediaLabel(entry)}</Typography>{entry.remarks ? <Typography color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>{entry.remarks}</Typography> : null}</Box>
              <Stack direction="row" spacing={0.5}>
                <Tooltip title="Original herunterladen"><IconButton onClick={handleDownload}><DownloadIcon /></IconButton></Tooltip>
                {selectedAnnotation ? <Button size="small" startIcon={<DownloadIcon />} onClick={handleMarkedDownload}>Markierte PNG</Button> : null}
              </Stack>
            </Stack>
            <Typography variant="caption" color="text.secondary">{index + 1} von {entries.length} · {entry.created_by_name}</Typography>
            {entry.asset_kind === 'image' ? (
              <Stack spacing={1} sx={{ mt: 1.5 }}>
                <Stack direction="row" spacing={0.75} sx={{ overflowX: 'auto', pb: 0.5 }}>
                  <Button size="small" variant={selectedAnnotationId === null ? 'contained' : 'outlined'} onClick={() => setSelectedAnnotationId(null)}>Original</Button>
                  {annotations.map((annotationItem, annotationIndex) => (
                    <Button key={annotationItem.id} size="small" variant={selectedAnnotationId === annotationItem.id ? 'contained' : 'outlined'} onClick={() => setSelectedAnnotationId(annotationItem.id)}>
                      {annotationItem.title || `Markierung ${annotationIndex + 1}`}
                    </Button>
                  ))}
                </Stack>
                {selectedAnnotation ? <Typography variant="caption" color="text.secondary">{selectedAnnotation.created_by_name} · {formatDateTime(selectedAnnotation.created_at)}</Typography> : null}
                {canManage ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button size="small" startIcon={<BrushIcon />} onClick={() => { setEditorAnnotation(null); setEditorOpen(true); }}>Neue Markierung</Button>
                    {selectedAnnotation ? <Button size="small" startIcon={<EditOutlinedIcon />} onClick={() => { setEditorAnnotation(selectedAnnotation); setEditorOpen(true); }}>Bearbeiten</Button> : null}
                    {selectedAnnotation ? <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={removeAnnotation}>Löschen</Button> : null}
                  </Stack>
                ) : null}
              </Stack>
            ) : null}
            {entries.length > 1 ? <Stack direction="row" spacing={0.75} sx={{ mt: 1.5, overflowX: 'auto', pb: 0.5 }}>{entries.map((item, itemIndex) => <Button key={item.id} size="small" variant={itemIndex === index ? 'contained' : 'outlined'} onClick={() => onIndexChange(itemIndex)} startIcon={item.asset_kind === 'video' ? <PlayCircleOutlineIcon /> : <ImageOutlinedIcon />}>{itemIndex + 1}</Button>)}</Stack> : null}
          </Box>
          <DataExchangeAnnotationEditor
            open={editorOpen}
            imageUrl={mediaUrl}
            annotation={editorAnnotation}
            saving={savingAnnotation}
            onClose={() => setEditorOpen(false)}
            onSave={saveAnnotation}
          />
        </>
      ) : null}
    </Dialog>
  );
}

function DataExchangePage() {
  const { user } = useAuth();
  const mayManage = canManageDataExchange(user);
  const [entries, setEntries] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [pending, setPending] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [slideIndex, setSlideIndex] = useState(null);
  const [editEntry, setEditEntry] = useState(null);
  const [deleteEntry, setDeleteEntry] = useState(null);
  const [guestDialogOpen, setGuestDialogOpen] = useState(false);
  const [guestLinks, setGuestLinks] = useState([]);
  const [guestLabel, setGuestLabel] = useState('');
  const [createdGuestUrl, setCreatedGuestUrl] = useState('');
  const [guestLinkBusy, setGuestLinkBusy] = useState(false);
  const [selectedAssetIds, setSelectedAssetIds] = useState(() => new Set());
  const [bulkDownloadBusy, setBulkDownloadBusy] = useState(false);
  const [bulkDownloadProgress, setBulkDownloadProgress] = useState(0);
  const [bulkDeleteMode, setBulkDeleteMode] = useState(null);
  const [bulkDeleteBusy, setBulkDeleteBusy] = useState(false);
  const [bulkDeleteError, setBulkDeleteError] = useState('');

  const loadEntries = async (requestedPage = 1, append = false) => {
    setLoading(true);
    setError('');
    try {
      const response = await listDataExchangeEntries(requestedPage);
      setEntries((current) => append ? [...current, ...response.data.entries] : response.data.entries);
      if (!append) setSelectedAssetIds(new Set());
      setPage(requestedPage);
      setHasMore(Boolean(response.data.hasMore));
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Datenaustausch konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void loadEntries(); }, []);

  const loadGuestLinks = async () => {
    if (!mayManage) return;
    try {
      const response = await listDataExchangeGuestLinks();
      setGuestLinks(response.data.links || []);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Gastzugänge konnten nicht geladen werden.');
    }
  };

  const openGuestDialog = () => {
    setGuestDialogOpen(true);
    setCreatedGuestUrl('');
    void loadGuestLinks();
  };

  const copyGuestUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setNotice('Gastzugang wurde in die Zwischenablage kopiert.');
      return true;
    } catch {
      setNotice('Automatisches Kopieren ist nicht verfügbar. Der Link kann im Feld kopiert werden.');
      return false;
    }
  };

  const createGuestLink = async () => {
    setGuestLinkBusy(true);
    try {
      const response = await createDataExchangeGuestLink(guestLabel);
      setCreatedGuestUrl(response.data.url);
      setGuestLabel('');
      await copyGuestUrl(response.data.url);
      await loadGuestLinks();
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Gastzugang konnte nicht erstellt werden.');
    } finally {
      setGuestLinkBusy(false);
    }
  };

  const revokeGuestLink = async (linkId) => {
    setGuestLinkBusy(true);
    try {
      await revokeDataExchangeGuestLink(linkId);
      setNotice('Gastzugang wurde widerrufen.');
      await loadGuestLinks();
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Gastzugang konnte nicht widerrufen werden.');
    } finally {
      setGuestLinkBusy(false);
    }
  };

  const addFiles = (event) => {
    const selected = Array.from(event.target.files || []);
    setPending((current) => [...current, ...selected.map((file) => ({ id: crypto.randomUUID(), file, title: '', remarks: '' }))]);
    event.target.value = '';
  };

  const updatePending = (id, field, value) => setPending((current) => current.map((item) => item.id === id ? { ...item, [field]: value } : item));

  const uploadPending = async () => {
    if (!pending.length) return;
    setUploading(true);
    setError('');
    try {
      for (let index = 0; index < pending.length; index += 1) {
        await uploadDataExchangeEntry(pending[index], (progressEvent) => {
          const fileProgress = progressEvent.total ? progressEvent.loaded / progressEvent.total : 0;
          setUploadProgress(Math.round(((index + fileProgress) / pending.length) * 100));
        });
      }
      setPending([]);
      setNotice(`${pending.length} ${pending.length === 1 ? 'Anhang wurde' : 'Anhänge wurden'} hochgeladen.`);
      await loadEntries();
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Upload fehlgeschlagen.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const saveEdit = async () => {
    try {
      const response = await updateDataExchangeEntry(editEntry.id, { title: editEntry.title, remarks: editEntry.remarks });
      setEntries((current) => current.map((item) => item.id === editEntry.id ? response.data.entry : item));
      setEditEntry(null);
      setNotice('Anhang wurde aktualisiert.');
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Änderung konnte nicht gespeichert werden.');
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteDataExchangeEntry(deleteEntry.id);
      setEntries((current) => current.filter((item) => item.id !== deleteEntry.id));
      setSelectedAssetIds((current) => {
        const next = new Set(current);
        next.delete(deleteEntry.id);
        return next;
      });
      setDeleteEntry(null);
      setSlideIndex(null);
      setNotice('Anhang wurde gelöscht.');
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Anhang konnte nicht gelöscht werden.');
    }
  };

  const toggleAssetSelection = (entryId) => {
    setSelectedAssetIds((current) => {
      const next = new Set(current);
      if (next.has(entryId)) next.delete(entryId);
      else next.add(entryId);
      return next;
    });
  };

  const loadAllEntries = async () => {
    const allEntries = [];
    let requestedPage = 1;
    let more = true;
    while (more) {
      const response = await listDataExchangeEntries(requestedPage, 100);
      allEntries.push(...response.data.entries);
      more = Boolean(response.data.hasMore);
      requestedPage += 1;
    }
    setEntries(allEntries);
    setPage(Math.max(1, requestedPage - 1));
    setHasMore(false);
    return allEntries;
  };

  const selectAllAssets = async () => {
    setBulkDownloadBusy(true);
    setError('');
    try {
      const allEntries = await loadAllEntries();
      const selectableEntries = mayManage
        ? allEntries
        : allEntries.filter((item) => item.asset_kind === 'image');
      setSelectedAssetIds(new Set(selectableEntries.map((item) => item.id)));
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Medien konnten nicht vollständig geladen werden.');
    } finally {
      setBulkDownloadBusy(false);
    }
  };

  const safeZipName = (entry) => {
    const originalName = String(entry.original_name || `foto-${entry.id}`);
    const baseName = originalName.replace(/\.[^.]+$/, '').replace(/[\\/:*?"<>|]/g, '_');
    const extension = originalName.match(/\.[^.]+$/)?.[0] || '.jpg';
    return `${baseName}-${entry.id}${extension}`;
  };

  const downloadSelectedPhotos = async () => {
    const selectedEntries = entries.filter((item) => item.asset_kind === 'image' && selectedAssetIds.has(item.id));
    if (!selectedEntries.length) return;
    setBulkDownloadBusy(true);
    setBulkDownloadProgress(0);
    setError('');
    try {
      const zip = new JSZip();
      for (let index = 0; index < selectedEntries.length; index += 1) {
        const selectedEntry = selectedEntries[index];
        const blobResponse = await getDataExchangeBlob(selectedEntry.id, true);
        zip.file(safeZipName(selectedEntry), blobResponse.data);
        setBulkDownloadProgress(Math.round(((index + 1) / selectedEntries.length) * 80));
      }
      const archive = await zip.generateAsync({ type: 'blob' }, (metadata) => {
        setBulkDownloadProgress(80 + Math.round(metadata.percent * 0.2));
      });
      const archiveUrl = URL.createObjectURL(archive);
      const anchor = document.createElement('a');
      anchor.href = archiveUrl;
      anchor.download = `datenaustausch-fotos-${new Date().toISOString().slice(0, 10)}.zip`;
      anchor.click();
      URL.revokeObjectURL(archiveUrl);
      setNotice(`${selectedEntries.length} Fotos wurden als ZIP bereitgestellt.`);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Fotos konnten nicht heruntergeladen werden.');
    } finally {
      setBulkDownloadBusy(false);
      setBulkDownloadProgress(0);
    }
  };

  const openBulkDeleteDialog = (mode) => {
    setBulkDeleteError('');
    setBulkDeleteMode(mode);
  };

  const confirmBulkDelete = async () => {
    setBulkDeleteBusy(true);
    setBulkDeleteError('');
    try {
      const payload = bulkDeleteMode === 'all'
        ? { scope: 'all' }
        : { ids: [...selectedAssetIds] };
      const response = await bulkDeleteDataExchangeEntries(payload);
      const deletedCount = Number(response.data.deletedCount || 0);
      const cleanupWarningCount = response.data.cleanupWarnings?.length || 0;
      setBulkDeleteMode(null);
      setSlideIndex(null);
      await loadEntries();
      setNotice(`${deletedCount} ${deletedCount === 1 ? 'Medium wurde' : 'Medien wurden'} gelöscht.`);
      if (cleanupWarningCount) {
        setError(`${cleanupWarningCount} temporäre ${cleanupWarningCount === 1 ? 'Datei konnte' : 'Dateien konnten'} nicht bereinigt werden. Bitte Administration informieren.`);
      }
    } catch (requestError) {
      setBulkDeleteError(requestError.response?.data?.error || 'Medien konnten nicht gelöscht werden.');
    } finally {
      setBulkDeleteBusy(false);
    }
  };

  const selectedPhotoCount = entries.filter((entry) => (
    entry.asset_kind === 'image' && selectedAssetIds.has(entry.id)
  )).length;

  return (
    <Stack spacing={2.5} sx={{ pt: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1.5}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>Datenaustausch</Typography>
          <Typography color="text.secondary">Fotos und Videos für Einsatzkräfte am Boden</Typography>
        </Box>
        {mayManage ? <Button variant="contained" color="secondary" startIcon={<LinkIcon />} onClick={openGuestDialog}>Gastzugang bereitstellen</Button> : null}
      </Stack>
      {error ? <Alert severity="error" onClose={() => setError('')}>{error}</Alert> : null}
      {notice ? <Alert severity="success" onClose={() => setNotice('')}>{notice}</Alert> : null}

      {mayManage ? (
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 1 }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1}>
              <Box><Typography variant="h6" sx={{ fontWeight: 700 }}>Medien bereitstellen</Typography><Typography variant="body2" color="text.secondary">Fotos und Videos bis 50 MB pro Datei</Typography></Box>
              <Button component="label" variant="outlined" startIcon={<AddPhotoAlternateIcon />} disabled={uploading}>Dateien auswählen<input hidden multiple type="file" accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/quicktime" onChange={addFiles} /></Button>
            </Stack>
            {pending.map((item) => (
              <Box key={item.id} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(180px,.8fr) 1fr 1.5fr auto' }, gap: 1.25, alignItems: 'start' }}>
                <Typography variant="body2" sx={{ pt: 1.5, overflowWrap: 'anywhere' }}>{item.file.name}</Typography>
                <TextField size="small" label="Überschrift (optional)" value={item.title} onChange={(event) => updatePending(item.id, 'title', event.target.value)} inputProps={{ maxLength: 255 }} />
                <TextField size="small" label="Bemerkung (optional)" multiline minRows={1} value={item.remarks} onChange={(event) => updatePending(item.id, 'remarks', event.target.value)} inputProps={{ maxLength: 5000 }} />
                <Tooltip title="Aus Auswahl entfernen"><IconButton onClick={() => setPending((current) => current.filter((pendingItem) => pendingItem.id !== item.id))}><CloseIcon /></IconButton></Tooltip>
              </Box>
            ))}
            {uploading ? <LinearProgress variant="determinate" value={uploadProgress} /> : null}
            {pending.length ? <Button variant="contained" startIcon={<UploadFileIcon />} onClick={uploadPending} disabled={uploading} sx={{ alignSelf: 'flex-start' }}>{pending.length} {pending.length === 1 ? 'Anhang' : 'Anhänge'} hochladen</Button> : null}
          </Stack>
        </Paper>
      ) : null}

      <Divider />
      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedAssetIds.size} {mayManage ? 'Medien' : 'Fotos'} ausgewählt</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button startIcon={<SelectAllIcon />} onClick={selectAllAssets} disabled={bulkDownloadBusy || bulkDeleteBusy}>{mayManage ? 'Alle auswählen' : 'Alle Fotos auswählen'}</Button>
            {selectedAssetIds.size ? <Button onClick={() => setSelectedAssetIds(new Set())} disabled={bulkDownloadBusy || bulkDeleteBusy}>Auswahl aufheben</Button> : null}
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={downloadSelectedPhotos} disabled={!selectedPhotoCount || bulkDownloadBusy || bulkDeleteBusy}>{selectedPhotoCount} Fotos als ZIP</Button>
            {mayManage && selectedAssetIds.size ? <Button color="error" variant="outlined" startIcon={<DeleteOutlineIcon />} onClick={() => openBulkDeleteDialog('selection')} disabled={bulkDownloadBusy || bulkDeleteBusy}>Auswahl löschen</Button> : null}
            {mayManage ? <Button color="error" variant="contained" startIcon={<DeleteOutlineIcon />} onClick={() => openBulkDeleteDialog('all')} disabled={!entries.length || bulkDownloadBusy || bulkDeleteBusy}>Alle Daten löschen</Button> : null}
          </Stack>
        </Stack>
        {bulkDownloadBusy && bulkDownloadProgress > 0 ? <LinearProgress variant="determinate" value={bulkDownloadProgress} sx={{ mt: 1.5 }} /> : null}
      </Paper>
      {loading && !entries.length ? <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress /></Box> : null}
      {!loading && !entries.length ? <Alert severity="info">Noch keine Fotos oder Videos vorhanden.</Alert> : null}
      {entries.length ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))', gap: 2 }}>
          {entries.map((entry, index) => <MediaCard key={entry.id} entry={entry} canManage={mayManage} selectable={mayManage || entry.asset_kind === 'image'} selected={selectedAssetIds.has(entry.id)} onToggleSelection={() => toggleAssetSelection(entry.id)} onOpen={() => setSlideIndex(index)} onEdit={() => setEditEntry({ ...entry })} onDelete={() => setDeleteEntry(entry)} />)}
        </Box>
      ) : null}
      {hasMore ? <Button variant="outlined" onClick={() => loadEntries(page + 1, true)} disabled={loading} sx={{ alignSelf: 'center' }}>Weitere Medien laden</Button> : null}

      <SlideshowDialog entries={entries} index={slideIndex} canManage={mayManage} onIndexChange={setSlideIndex} onClose={() => setSlideIndex(null)} />
      <Dialog open={Boolean(editEntry)} onClose={() => setEditEntry(null)} fullWidth maxWidth="sm">
        <DialogTitle>Anhang bearbeiten</DialogTitle>
        <DialogContent><Stack spacing={2} sx={{ pt: 1 }}><TextField label="Überschrift" value={editEntry?.title || ''} onChange={(event) => setEditEntry((current) => ({ ...current, title: event.target.value }))} inputProps={{ maxLength: 255 }} /><TextField label="Bemerkung" multiline minRows={4} value={editEntry?.remarks || ''} onChange={(event) => setEditEntry((current) => ({ ...current, remarks: event.target.value }))} inputProps={{ maxLength: 5000 }} /></Stack></DialogContent>
        <DialogActions><Button onClick={() => setEditEntry(null)}>Abbrechen</Button><Button variant="contained" onClick={saveEdit}>Speichern</Button></DialogActions>
      </Dialog>
      <Dialog open={Boolean(deleteEntry)} onClose={() => setDeleteEntry(null)}>
        <DialogTitle>Anhang löschen?</DialogTitle><DialogContent><Typography>„{deleteEntry ? mediaLabel(deleteEntry) : ''}“ wird dauerhaft gelöscht.</Typography></DialogContent>
        <DialogActions><Button onClick={() => setDeleteEntry(null)}>Abbrechen</Button><Button color="error" variant="contained" onClick={confirmDelete}>Löschen</Button></DialogActions>
      </Dialog>
      <DataExchangeBulkDeleteDialog
        mode={bulkDeleteMode}
        selectedCount={selectedAssetIds.size}
        deleting={bulkDeleteBusy}
        error={bulkDeleteError}
        onClose={() => setBulkDeleteMode(null)}
        onConfirm={confirmBulkDelete}
      />
      <Dialog open={guestDialogOpen} onClose={() => setGuestDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Gastzugang bereitstellen</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">Der Link ist 7 Tage gültig, mehrfach nutzbar und bietet ausschließlich Lesezugriff auf den Datenaustausch.</Alert>
            <TextField label="Bezeichnung (optional)" value={guestLabel} onChange={(event) => setGuestLabel(event.target.value)} inputProps={{ maxLength: 255 }} placeholder="z. B. Einsatzleitung Wache 3" />
            <Button variant="contained" startIcon={<LinkIcon />} onClick={createGuestLink} disabled={guestLinkBusy}>Link erzeugen und kopieren</Button>
            {createdGuestUrl ? (
              <TextField
                label="Erstellter Gastlink"
                value={createdGuestUrl}
                InputProps={{ readOnly: true, endAdornment: <Tooltip title="Link kopieren"><IconButton onClick={() => copyGuestUrl(createdGuestUrl)}><ContentCopyIcon /></IconButton></Tooltip> }}
                onFocus={(event) => event.target.select()}
              />
            ) : null}
            <Divider />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Bisherige Gastzugänge</Typography>
            {!guestLinks.length ? <Typography color="text.secondary">Noch keine Gastzugänge vorhanden.</Typography> : null}
            {guestLinks.map((link) => (
              <Box key={link.id} sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 1, alignItems: 'center', py: 1, borderBottom: 1, borderColor: 'divider' }}>
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>{link.label || 'Gastzugang'}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {link.status === 'active' ? `Gültig bis ${formatDateTime(link.expires_at)}` : link.status === 'revoked' ? 'Widerrufen' : 'Abgelaufen'}
                    {' · '}{link.use_count} Anmeldungen{link.last_used_at ? ` · zuletzt ${formatDateTime(link.last_used_at)}` : ''}
                  </Typography>
                </Box>
                {link.status === 'active' ? <Tooltip title="Gastzugang widerrufen"><IconButton color="error" onClick={() => revokeGuestLink(link.id)} disabled={guestLinkBusy}><LinkOffIcon /></IconButton></Tooltip> : null}
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setGuestDialogOpen(false)}>Schließen</Button></DialogActions>
      </Dialog>
    </Stack>
  );
}

export default DataExchangePage;